import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { resolveImagePath, FALLBACK_IMAGE } from "./imagePath";

export default function ItemListScreen() {
  const [items, setItems] = useState([]);
  const [currentSearchQueryDisplay, setCurrentSearchQueryDisplay] = useState("");
  const [searchBarInput, setSearchBarInput] = useState(""); // For the input field on this screen
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const apiKey = process.env.REACT_APP_FOODGYDE_API_KEY; // Get from React env var, set in .env (see .env.example)

  useEffect(() => {
    const searchState = location.state;
    console.log("ItemListScreen - useEffect triggered. Received state:", searchState);
    setIsLoading(true); // Set loading true at the start of a fetch
    setError(null);     // Clear previous errors

    if (!searchState || typeof searchState.query === 'undefined') {
      console.log("ItemListScreen - No search state or query found, not fetching.");
      setError("Search parameters missing. Please start a new search from the home page.");
      setItems([]);
      setIsLoading(false);
      return;
    }

    const {
      query = "",
      ingredients = [],
      certifications = {}
    } = searchState;

    setCurrentSearchQueryDisplay(query);
    if (!searchBarInput && query) { // Initialize search bar only if it's empty and we have a query
        setSearchBarInput(query);
    }


    console.log("ItemListScreen - Making POST request to API with payload:", { query, ingredients, certifications });

    axios
      .post("https://api.foodgyde.com/search", { // Updated API URL
        query,
        ingredients,
        certifications
      }, {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey// If using API key
        }
      })
      .then((res) => {
        console.log("ItemListScreen - Response from /search API:", res);
        console.log("ItemListScreen - Data from /search API:", res.data);
        if (Array.isArray(res.data?.items)) {
          setItems(res.data.items);
        } else {
          console.error("ItemListScreen - Data from /search is missing an items array:", res.data);
          setItems([]);
          setError("Received invalid item data from server.");
        }
      })
      .catch((err) => {
        console.error("ItemListScreen - Error fetching items (axios catch block):", err.response || err.message || err);
        setError("Unable to fetch items. Please check API status or network.");
        setItems([]);
      })
      .finally(() => {
        setIsLoading(false); // Set loading false after fetch completes or fails
      });
  }, [location.state]); // Re-run effect if location.state changes

  const handleLocalSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedQuery = searchBarInput.trim();
    if (trimmedQuery === "") return;

    console.log("ItemListScreen - Performing new local search with query:", trimmedQuery);
    // For a new search from this screen, pass new state to itself to re-trigger useEffect
    navigate('/items', { 
      state: {
        query: trimmedQuery,
        // Carry over existing filter *types* but they would be empty unless you have UI to set them here
        // Or, you might want to reset filters for a new search from this bar
        ingredients: [], // Example: Resetting ingredients filter
        certifications: {} // Example: Resetting certifications
      },
      replace: true // Optional: to replace the current history entry
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="w-full bg-blue-600 p-4 text-white text-center text-lg font-bold">
        Item List
      </header>

      <div className="p-4 bg-white shadow-md sticky top-0 z-10"> {/* Made search bar sticky */}
        <form onSubmit={handleLocalSearchSubmit} className="flex items-center gap-4 max-w-3xl mx-auto"> {/* Centered search bar */}
          <input
            type="text"
            value={searchBarInput}
            onChange={(e) => setSearchBarInput(e.target.value)}
            placeholder="Search within results or new search..."
            className="flex-grow p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700"
          >
            Search
          </button>
        </form>
      </div>

      <main className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6"> {/* Responsive grid */}
        {isLoading ? (
          <div className="col-span-full text-center text-gray-500 py-10">Loading items...</div>
        ) : error ? (
          <div className="col-span-full text-center text-red-600 py-10">Error: {error}</div>
        ) : items.length === 0 && location.state ? (
          <div className="col-span-full text-center text-gray-500 py-10">
            No items found for "{currentSearchQueryDisplay}".
          </div>
        ) : !location.state && items.length === 0 ? (
          <div className="col-span-full text-center text-gray-500 py-10">
            Please perform a search from the home page to see items.
          </div>
        ) : (
          items.map((item) => {
            const firstImage = item.imagePath ? resolveImagePath(item.imagePath.split(",")[0]) : null;

            return (
              <div
                key={item.id}
                onClick={() => navigate(`/item/${item.id}`)}
                className="bg-white p-4 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 ease-in-out cursor-pointer flex flex-col items-center text-center"
              >
                {firstImage ? (
                  <img
                    src={firstImage} // Should be like "/images/milk-1.jpg"
                    alt={item.name || "Item image"}
                    className="w-32 h-32 object-cover rounded-md mb-3"
                    onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; console.warn(`Failed to load image: ${firstImage}`)}}
                  />
                ) : (
                  <div className="w-32 h-32 bg-gray-200 rounded-md mb-3 flex items-center justify-center text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div className="flex-grow w-full"> {/* Ensure text container takes width */}
                  <h2 className="text-md font-semibold text-gray-800 truncate w-full" title={item.name}>{item.name || "Unnamed Item"}</h2>
                  <p className="mt-1 text-blue-600 font-bold">
                    {item.price ? `$${parseFloat(item.price).toFixed(2)}` : "N/A"}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </main>
    </div>
  );
}